import React, { useState, useEffect } from 'react';
import { 
  Server, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Wifi, 
  WifiOff, 
  Play, 
  Pause, 
  RefreshCw,
  AlertCircle,
  Settings,
  Monitor
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { apiRequest } from '../services/api.ts';

interface EdgeDevice {
  device_id: string;
  device_info: {
    hostname: string;
    ip_address: string;
    mac_address: string;
    location: string;
    description: string;
  };
  status: 'online' | 'offline' | 'pending' | 'error';
  last_seen?: string;
  connection_count?: number;
  approved_at?: string;
  current_ip?: string;
  sync_enabled?: boolean;
}

interface PendingDevice {
  device_id: string;
  device_info: {
    hostname: string;
    ip_address: string;
    mac_address: string;
    location: string;
    description: string;
  };
  created_at: string;
}

export function EdgeDeviceManagement() {
  const [devices, setDevices] = useState<EdgeDevice[]>([]);
  const [pendingDevices, setPendingDevices] = useState<PendingDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(false);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('csense-theme');
    setDarkMode(savedTheme === 'dark');

    const handleThemeChange = (event: CustomEvent) => {
      setDarkMode(event.detail.theme === 'dark');
    };

    document.addEventListener('themeChange', handleThemeChange as EventListener);
    return () => {
      document.removeEventListener('themeChange', handleThemeChange as EventListener);
    };
  }, []);

  // Load devices on component mount
  useEffect(() => {
    loadDevices();
    loadPendingDevices();
    
    // Set up polling for real-time updates
    const interval = setInterval(() => {
      loadDevices();
      loadPendingDevices();
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const loadDevices = async () => {
    try {
      const response = await apiRequest('/api/edge/devices');
      setDevices(response.devices || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load edge devices:', err);
      setError('Failed to load edge devices');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingDevices = async () => {
    try {
      const response = await apiRequest('/api/edge/devices/pending');
      setPendingDevices(response.devices || []);
    } catch (err) {
      console.error('Failed to load pending devices:', err);
    }
  };

  const approveDevice = async (deviceId: string) => {
    try {
      await apiRequest(`/api/edge/devices/${deviceId}/approve`, {
        method: 'POST'
      });
      
      // Refresh both lists
      await loadDevices();
      await loadPendingDevices();
      
      // Show success notification
      showNotification(`Device ${deviceId} approved successfully`, 'success');
    } catch (err) {
      console.error('Failed to approve device:', err);
      showNotification('Failed to approve device', 'error');
    }
  };

  const rejectDevice = async (deviceId: string) => {
    const reason = prompt('Enter rejection reason (optional):') || '';
    
    try {
      await apiRequest(`/api/edge/devices/${deviceId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
      
      // Refresh pending devices list
      await loadPendingDevices();
      
      // Show success notification
      showNotification(`Device ${deviceId} rejected`, 'success');
    } catch (err) {
      console.error('Failed to reject device:', err);
      showNotification('Failed to reject device', 'error');
    }
  };

  const toggleSync = async (deviceId: string, enable: boolean) => {
    try {
      await apiRequest(`/api/edge/devices/${deviceId}/sync`, {
        method: 'POST',
        body: JSON.stringify({ enable })
      });
      
      // Update local state
      setDevices(prev => prev.map(device => 
        device.device_id === deviceId 
          ? { ...device, sync_enabled: enable }
          : device
      ));
      
      showNotification(`Sync ${enable ? 'enabled' : 'disabled'} for device ${deviceId}`, 'success');
    } catch (err) {
      console.error('Failed to toggle sync:', err);
      showNotification('Failed to toggle sync', 'error');
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    // Emit notification event for the notification system
    document.dispatchEvent(new CustomEvent('edgeDeviceNotification', {
      detail: { message, type }
    }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <Wifi className="h-4 w-4 text-green-500" />;
      case 'offline':
        return <WifiOff className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      online: 'bg-green-100 text-green-800 border-green-200',
      offline: 'bg-red-100 text-red-800 border-red-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      error: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <Badge className={`${variants[status as keyof typeof variants]} border`}>
        {getStatusIcon(status)}
        <span className="ml-1 capitalize">{status}</span>
      </Badge>
    );
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return 'Never';
    
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className={`p-6 ${darkMode ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'}`}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2">Loading edge devices...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${darkMode ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Server className="h-6 w-6 text-blue-500" />
            Edge Device Management
          </h1>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            Manage and monitor your edge detection devices
          </p>
        </div>
        <Button 
          onClick={() => { loadDevices(); loadPendingDevices(); }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Card className={`border-red-200 ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Devices</p>
                <p className="text-2xl font-bold">{devices.length}</p>
              </div>
              <Server className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Online</p>
                <p className="text-2xl font-bold text-green-500">
                  {devices.filter(d => d.status === 'online').length}
                </p>
              </div>
              <Wifi className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Offline</p>
                <p className="text-2xl font-bold text-red-500">
                  {devices.filter(d => d.status === 'offline').length}
                </p>
              </div>
              <WifiOff className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pending</p>
                <p className="text-2xl font-bold text-yellow-500">{pendingDevices.length}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Devices */}
      {pendingDevices.length > 0 && (
        <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Pending Approval ({pendingDevices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingDevices.map((device) => (
                <div 
                  key={device.device_id}
                  className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Server className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{device.device_info.hostname}</h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {device.device_info.ip_address} • {device.device_info.location}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Requested: {new Date(device.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => approveDevice(device.device_id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        size="sm"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => rejectDevice(device.device_id)}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        size="sm"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Devices */}
      <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-blue-500" />
            Active Devices ({devices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <div className="text-center py-8">
              <Server className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No edge devices registered
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                Edge devices will appear here after registration and approval
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {devices.map((device) => (
                <div 
                  key={device.device_id}
                  className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          device.status === 'online' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          <Server className={`h-5 w-5 ${
                            device.status === 'online' ? 'text-green-600' : 'text-red-600'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{device.device_info.hostname}</h3>
                            {getStatusBadge(device.status)}
                          </div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {device.current_ip || device.device_info.ip_address} • {device.device_info.location}
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            Last seen: {formatLastSeen(device.last_seen)} • 
                            Connections: {device.connection_count || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => toggleSync(device.device_id, !device.sync_enabled)}
                        variant="outline"
                        size="sm"
                        className={device.sync_enabled ? 'border-green-300 text-green-600' : 'border-gray-300'}
                      >
                        {device.sync_enabled ? (
                          <>
                            <Pause className="h-4 w-4 mr-1" />
                            Disable Sync
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-1" />
                            Enable Sync
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => window.open(`http://${device.current_ip || device.device_info.ip_address}:9000`, '_blank')}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600"
                      >
                        <Monitor className="h-4 w-4 mr-1" />
                        Local GUI
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-300"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Configure
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card className={darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}>
        <CardHeader>
          <CardTitle>Edge Device Setup</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-600">1</span>
              </div>
              <div>
                <h4 className="font-medium">Download and Setup</h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Download the edge agent software and run the setup script on your edge device.
                </p>
                <code className={`text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} px-2 py-1 rounded mt-1 block`}>
                  python3 setup_edge_device.py
                </code>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-600">2</span>
              </div>
              <div>
                <h4 className="font-medium">Device Registration</h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  The device will appear in the "Pending Approval" section above. Click "Approve" to activate it.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-600">3</span>
              </div>
              <div>
                <h4 className="font-medium">Local Configuration</h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Access the local GUI at http://[device-ip]:9000 to configure cameras and detection settings.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}